<template>
  <Teleport to="body">
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[2200] bg-black/70 backdrop-blur-sm p-3 sm:p-6"
        @click="closeModal"
      >
        <div
          class="mx-auto h-full w-full max-w-[1200px] rounded-xl border border-zinc-200 bg-white shadow-2xl overflow-hidden flex flex-col"
          @click.stop
        >
          <div class="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
            <h3 class="text-sm sm:text-base font-semibold text-zinc-800">
              {{ title || 'Course Certificate' }}
            </h3>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="rounded-md border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                :disabled="!downloadUrl"
                @click="downloadCertificate"
              >
                Download PDF
              </button>
              <button
                type="button"
                class="rounded-md border border-zinc-300 px-2.5 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                @click="closeModal"
              >
                Close
              </button>
            </div>
          </div>

          <div class="relative flex-1 bg-zinc-100">
            <div
              v-if="loading"
              class="absolute inset-0 z-10 flex items-center justify-center text-sm font-medium text-zinc-500 bg-white/70"
            >
              Loading certificate...
            </div>
            <div
              v-else-if="errorMessage"
              class="absolute inset-0 z-10 flex items-center justify-center text-sm font-medium text-red-600 bg-white/85 px-6 text-center"
            >
              {{ errorMessage }}
            </div>
            <iframe
              v-show="!loading && !errorMessage"
              class="h-full w-full border-0 bg-white"
              :src="resolvedPdfSrc"
              title="Course certificate viewer"
            />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  pdfSrc: {
    type: String,
    default: '',
  },
  downloadUrl: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: 'Course Certificate',
  },
});

const emit = defineEmits(['update:open']);

const loading = ref(false);
const errorMessage = ref('');
const resolvedPdfSrc = ref('');
let currentBlobUrl = '';

const closeModal = () => {
  emit('update:open', false);
};

const revokeBlobUrl = () => {
  if (!currentBlobUrl) return;
  try {
    URL.revokeObjectURL(currentBlobUrl);
  } catch (_err) {
    // no-op
  }
  currentBlobUrl = '';
};

const loadPdfForPreview = async () => {
  const sourceUrl = String(props.pdfSrc || '').trim();
  if (!props.open || !sourceUrl) {
    loading.value = false;
    errorMessage.value = '';
    resolvedPdfSrc.value = '';
    revokeBlobUrl();
    return;
  }

  loading.value = true;
  errorMessage.value = '';
  try {
    const response = await fetch(sourceUrl, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to load certificate (${response.status})`);
    }
    const blob = await response.blob();
    revokeBlobUrl();
    currentBlobUrl = URL.createObjectURL(blob);
    resolvedPdfSrc.value = currentBlobUrl;
  } catch (err) {
    resolvedPdfSrc.value = '';
    errorMessage.value = err?.message || 'Unable to load certificate preview';
  } finally {
    loading.value = false;
  }
};

const downloadCertificate = () => {
  if (!props.downloadUrl) return;
  const anchor = document.createElement('a');
  anchor.href = props.downloadUrl;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

watch(
  () => [props.open, props.pdfSrc],
  () => {
    loadPdfForPreview();
  },
  { immediate: true }
);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) return;
    loading.value = false;
    errorMessage.value = '';
    resolvedPdfSrc.value = '';
    revokeBlobUrl();
  }
);

onBeforeUnmount(() => {
  revokeBlobUrl();
});
</script>
